import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LIVE_MODE, mocApi } from '../api/client';
import {
  SquaresFour, PlusCircle, Folders, CheckSquare, ListChecks, ShieldCheck,
  Bell, BookOpen, SignOut, List as ListIcon, X, WifiSlash, GearSix, Question,
} from '@phosphor-icons/react';
import { Button } from './ui/button';

const NAV = [
  { to: '/dashboard', label: 'Tableau de bord', icon: SquaresFour },
  { to: '/moc/new', label: 'Nouvelle MOC', icon: PlusCircle },
  { to: '/mocs?scope=mine', label: 'Mes MOC', icon: Folders, match: '/mocs' },
  { to: '/validations', label: 'Mes validations', icon: CheckSquare },
  { to: '/actions', label: 'Mes actions', icon: ListChecks },
  { to: '/pssr', label: 'PSSR', icon: ShieldCheck },
  { to: '/notifications', label: 'Notifications', icon: Bell },
  { to: '/register', label: 'Registre MOC', icon: BookOpen },
  { to: '/admin', label: 'Administration', icon: GearSix },
  { to: '/aide', label: 'Aide', icon: Question },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    mocApi.listNotifications().then((ns) => setUnread((ns || []).filter((n) => !n.read).length)).catch(() => {});
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className={`fixed z-40 inset-y-0 left-0 w-64 bg-[hsl(217,60%,15%)] text-slate-100 transform transition-transform duration-200 md:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-sm border-2 border-sky-400 flex items-center justify-center font-black text-sm tracking-wide">MOC</div>
          <div className="leading-tight">
            <div className="font-display font-bold text-sm">Gestion du changement</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sky-300/80">Lafarge · Geocycle</div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              data-testid={`nav-${n.to.replace(/[/?=]/g, '-')}`}
              className={({ isActive }) => {
                const active = n.match ? window.location.pathname.startsWith(n.match) : isActive;
                return `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${active ? 'bg-sky-500/20 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`;
              }}>
              <n.icon size={18} weight="bold" />
              <span className="flex-1">{n.label}</span>
              {n.to === '/notifications' && unread > 0 && (
                <span data-testid="notif-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-sky-500 text-white text-[11px] font-bold flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-sky-500/30 flex items-center justify-center text-xs font-bold">
              {(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate">{user?.name || 'Utilisateur'}</div>
              <div className="text-[10px] text-slate-400 truncate">{user?.email}</div>
            </div>
            <button data-testid="logout-button" onClick={handleLogout} className="text-slate-300 hover:text-white">
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0">
        <header className="h-16 sticky top-0 z-20 bg-card/95 backdrop-blur border-b border-border flex items-center justify-between px-4 md:px-6">
          <button className="md:hidden" data-testid="menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <ListIcon size={22} />}
          </button>
          <div className="flex items-center gap-3 ml-auto">
            {!LIVE_MODE && (
              <span data-testid="demo-mode-badge" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300 px-2.5 py-1 rounded-sm">
                <WifiSlash size={14} weight="bold" /> Données de démonstration
              </span>
            )}
            <Button size="sm" data-testid="header-new-moc" onClick={() => navigate('/moc/new')}>
              <PlusCircle size={16} weight="bold" className="mr-1.5" /> Nouvelle MOC
            </Button>
          </div>
        </header>
        <main className="p-4 md:p-6 max-w-[1400px]">{children}</main>
      </div>
    </div>
  );
}
